// xterm.js wrapper for the Console tab. Bundled into the lazy Linux bundle only. Fitting is manual
// (no addon): the cell size comes from xterm's own measure element and a ResizeObserver refits.
// Input leaves as UTF-8 bytes; output arrives as bytes from the guest's serial port / console.
// Ported from EndisOS src/linux/v86/terminal.ts.
import { Terminal } from "@xterm/xterm";

const MEASURE = "W".repeat(32);

export function fitDimensions(width, height, cellWidth, cellHeight, scrollbar = 0) {
	if (cellWidth <= 0 || cellHeight <= 0) return { cols: 80, rows: 24 };
	return { cols: Math.max(2, Math.floor((width - scrollbar) / cellWidth)), rows: Math.max(1, Math.floor(height / cellHeight)) };
}

// Builds an xterm theme from Re:Vault's CSS variables (so the console follows the user's theme).
export function themeFromCss(host) {
	const st = getComputedStyle(host);
	const read = (n, f) => st.getPropertyValue(n).trim() || f;
	const background = read("--surface", "#0c0c0c");
	const foreground = read("--fg", "#ededed");
	const accent = read("--accent", "#e9c46a");
	return { background, foreground, cursor: accent, cursorAccent: background, selectionBackground: accent + "55" };
}

export function createTerminal(host, { fontSize = 13 } = {}) {
	const lineHeight = 1.15;
	const reduceMotion = typeof matchMedia === "function" && matchMedia("(prefers-reduced-motion: reduce)").matches;
	const term = new Terminal({
		convertEol: false, cursorBlink: !reduceMotion, scrollback: 3000, fontSize, lineHeight,
		// Gives screen readers a live text mirror of the terminal and announces new output.
		screenReaderMode: true,
		fontFamily: 'ui-monospace, "SFMono-Regular", "Cascadia Mono", Menlo, Consolas, monospace',
		theme: themeFromCss(host), allowTransparency: false, allowProposedApi: false,
	});
	const element = document.createElement("div");
	element.className = "rv-lx-term";
	host.append(element);
	term.open(element);
	// Tab belongs to the shell (completion), so Esc then Tab leaves the terminal instead, like the
	// Escape-then-Tab convention in code editors. Esc still reaches the shell either way.
	let escAt = 0;
	term.attachCustomKeyEventHandler((e) => {
		if (e.type !== "keydown") return true;
		if (e.key === "Escape") { escAt = Date.now(); return true; }
		if (e.key === "Tab" && !e.ctrlKey && !e.altKey && !e.metaKey && Date.now() - escAt < 1500) { escAt = 0; return false; }
		escAt = 0;
		return true;
	});
	const encoder = new TextEncoder();
	let locked = false; // CheerpX: the console size can't change after setCustomConsole
	// Prefer the cell size xterm actually rendered (.xterm-screen is exactly cols x rows cells); an
	// estimate from the measure element can be a pixel short per row, which made the terminal taller
	// than its panel and hid the last lines.
	const measure = () => {
		const screen = element.querySelector(".xterm-screen");
		const sr = screen && screen.getBoundingClientRect();
		if (sr && sr.width && sr.height && term.cols && term.rows) return { cellWidth: sr.width / term.cols, cellHeight: sr.height / term.rows };
		const nodes = Array.from(element.querySelectorAll(".xterm-char-measure-element"));
		const plain = nodes.find((n) => n.textContent === MEASURE && !n.style.fontWeight && !n.style.fontStyle) || nodes[0];
		const r = plain && plain.getBoundingClientRect();
		if (!r || !r.width) return { cellWidth: 0, cellHeight: 0 };
		return { cellWidth: r.width / 32, cellHeight: Math.ceil(r.height * lineHeight) + 1 };
	};
	const fit = () => {
		if (locked) return;
		const r = host.getBoundingClientRect();
		if (!r.width || !r.height) return;
		const { cellWidth, cellHeight } = measure();
		if (!cellWidth) return;
		const vp = element.querySelector(".xterm-viewport");
		const scrollbar = vp ? Math.max(0, vp.offsetWidth - vp.clientWidth) : 0;
		const { cols, rows } = fitDimensions(r.width - 16, r.height - 12, cellWidth, cellHeight, scrollbar);
		if (cols !== term.cols || rows !== term.rows) term.resize(cols, rows);
	};
	// Fonts can finish loading after the first fit; refit once they have.
	if (document.fonts && document.fonts.ready) document.fonts.ready.then(() => fit()).catch(() => {});
	const ro = typeof ResizeObserver !== "undefined" ? new ResizeObserver(() => fit()) : null;
	if (ro) ro.observe(host);
	fit();
	const subs = [];
	return {
		element, term,
		get cols() { return term.cols; },
		get rows() { return term.rows; },
		write: (bytes) => term.write(bytes),
		writeln: (text) => term.write(String(text).replace(/\n/g, "\r\n") + "\r\n"),
		onData(fn) { const d = term.onData((text) => fn(encoder.encode(text))); subs.push(d); return () => d.dispose(); },
		onBinary(fn) { const d = term.onBinary((s) => fn(Uint8Array.from(s, (c) => c.charCodeAt(0) & 255))); subs.push(d); return () => d.dispose(); },
		fit,
		lock(v) { locked = !!v; if (!locked) fit(); },
		focus: () => term.focus(),
		clear: () => { term.clear(); term.reset(); },
		applyTheme: () => { term.options.theme = themeFromCss(host); },
		text: () => { const b = term.buffer.active; const out = []; for (let i = 0; i < b.length; i++) { const l = b.getLine(i); if (l) out.push(l.translateToString(true)); } return out.join("\n"); },
		dispose() { if (ro) ro.disconnect(); subs.forEach((d) => { try { d.dispose(); } catch (e) {} }); term.dispose(); element.remove(); },
	};
}
